import {LitElement, html} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import {Service} from '../../service/service.js';
import {ElementUtil} from '../../utils/elementUtil.js';
import {TimeUtil} from '../../utils/timeUtil.js';
import {BaseComponent} from '../basecomponent.js';


/* global customElements */
export class NodeOverview extends BaseComponent {
	
	constructor() {
		super();
		
		var nodeOverview = this;
		setTimeout(function(){
			nodeOverview.init();
		});
	}
	
	render() {	
		return html`
			<slot></slot>		
		`;
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.stopMainAutoRefresh();
	}

	stopMainAutoRefresh() {
		if (this._mainAutoRefreshIntervalId) {
			clearInterval(this._mainAutoRefreshIntervalId);
			this._mainAutoRefreshIntervalId = null;
		}
	}
	
	init() {
		var nodeOverview = this;

		var AUTO_REFRESH_MS = 10000;
		var MAX_TREND_POINTS = 100;
		var SCOPE_CLUSTER = 'Cluster';
		var CHART_TYPE_LINE = 'line';
		var CHART_TYPE_FILL_LINE = 'fillLine';
		var DATASET_COLORS = [
			'#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F',
			'#EDC948', '#B07AA1', '#FF9DA7', '#9C755F', '#BAB0AC'
		];

		var latestNodes = [];

		var isNodeLive = function(node) {
			return !!(node && node.online);
		};

		var isNodeHealthy = function(node) {
			return isNodeLive(node) && !node.systemDisabled;
		};

		var memoryPercent = function(node) {
			if (!node || !node.maxMemory) {
				return 0;
			}
			var percent = (Number(node.useMemory) / Number(node.maxMemory)) * 100;
			if (isNaN(percent)) {
				return 0;
			}
			return percent;
		};

		var computeClusterSummary = function(nodes) {
			var total = (nodes || []).length;
			var live = 0;
			var healthy = 0;
			var minUpTime = null;
			var maxUpTime = null;
			var earliestStart = null;

			(nodes || []).forEach(function(node) {
				if (isNodeLive(node)) {
					live++;
				}
				if (isNodeHealthy(node)) {
					healthy++;
				}
				if (!isNodeLive(node)) {
					return;
				}
				var upTime = Number(node.upTime);
				if (!isNaN(upTime)) {
					if (minUpTime === null || upTime < minUpTime) {
						minUpTime = upTime;
					}
					if (maxUpTime === null || upTime > maxUpTime) {
						maxUpTime = upTime;
					}
				}
				var startTime = TimeUtil.parseDate(node.startTime);
				if (startTime) {
					if (earliestStart === null || startTime.getTime() < earliestStart.getTime()) {
						earliestStart = startTime;
					}
				}
			});

			return {
				total: total,
				live: live,
				healthy: healthy,
				minUpTime: minUpTime,
				maxUpTime: maxUpTime,
				earliestStart: earliestStart
			};
		};

		var computeStatsSample = function(nodes, scopeNodeId) {
			var timestamp = new Date();
			if (scopeNodeId && scopeNodeId !== SCOPE_CLUSTER) {
				var matched = null;
				(nodes || []).some(function(node) {
					if (node.nodeId === scopeNodeId) {
						matched = node;
						return true;
					}
					return false;
				});
				if (!matched || !isNodeLive(matched)) {
					return null;
				}
				return {
					timestamp: timestamp,
					cpuPct: Number(matched.cpuPercentage) || 0,
					memoryPct: memoryPercent(matched),
					threads: Number(matched.liveThreadCount) || 0
				};
			}

			var onlineNodes = (nodes || []).filter(isNodeLive);
			if (onlineNodes.length === 0) {
				return null;
			}
			var cpuTotal = 0;
			var memoryTotal = 0;
			var threadsTotal = 0;
			onlineNodes.forEach(function(node) {
				cpuTotal += Number(node.cpuPercentage) || 0;
				memoryTotal += memoryPercent(node);
				threadsTotal += Number(node.liveThreadCount) || 0;
			});
			var count = onlineNodes.length;
			return {
				timestamp: timestamp,
				cpuPct: cpuTotal / count,
				memoryPct: memoryTotal / count,
				threads: threadsTotal / count
			};
		};

		var appendTrendPoint = function(buffer, sample, maxPoints) {
			if (!sample) {
				return buffer;
			}
			buffer.push(sample);
			var limit = maxPoints || MAX_TREND_POINTS;
			while (buffer.length > limit) {
				buffer.shift();
			}
			return buffer;
		};

		var toolbar = ElementUtil.create({
			tag: 'dl-toolbar',
			slot: 'toolbar',
			theme: 'spacing',
			children: [
				{
					tag: 'vaadin-button',
					itemid: 'refresh',
					'class': 'spacer-toolbar-item',
					children:[
						ElementUtil.makeIcon('vaadin:refresh', ' Refresh')
					], 
					listeners: {
						click: function() {
							actionRefresh();
						}
					}
				},
				{
					tag: 'vaadin-checkbox',
					itemid: 'autoRefresh',
					'class': 'spacer-toolbar-item',
					label: 'Auto refresh (10s)',
					listeners: {
						change: function(event) {
							if (event.target.checked) {
								nodeOverview.stopMainAutoRefresh();
								nodeOverview._mainAutoRefreshIntervalId = setInterval(function() {
									actionRefresh({ silent: true });
								}, AUTO_REFRESH_MS);
							} else {
								nodeOverview.stopMainAutoRefresh();
							}
						}
					}
				},
				{
					tag: 'vaadin-button',
					itemid: 'visualize',
					'class': 'spacer-toolbar-item',
					children:[
						ElementUtil.makeIcon('vaadin:chart', ' Visualize')
					],
					listeners: {
						click: function() {
							actionShowVisualization();
						}
					}
				},
				{
					tag: 'vaadin-button',
					itemid: 'downloadLogs',
					'class': 'spacer-toolbar-item',
					style: 'float: right;',
					permissions: ['ADMIN_SYSTEM:READ'],
					children:[
						ElementUtil.makeIcon('vaadin:download', ' Download Logs')
					], 
					listeners: {
						click: function() {
							actionDownloadLogs();
						}
					}
				}
			]
		});

		var summaryStrip = ElementUtil.create({
			tag: 'div',
			itemid: 'clusterSummary',
			'class': 'cluster-summary'
		});
		
		var nodeCardsContainer = ElementUtil.create({
			tag: 'div',
			itemid: 'nodeCards'
		});

		var nodeContainer = ElementUtil.create({
			tag: 'div',
			slot: 'primary',
			style: 'height: 100%; overflow: auto',
			children: [
				summaryStrip,
				nodeCardsContainer
			]
		});
		
		
		var mainContent = ElementUtil.create({
			tag: 'dl-flex-layout',
			children: [	
				toolbar,
				nodeContainer,				
				{
					tag: 'style',
					innerHTML: `					
						.node-id {
							font-size: 18px;
							font-weight: bold;
						}
						.node-detail {
							color: #585858;
						}
						.node-status {
							background-color: yellow;
							padding: 5px;
							text-align: center;
							border-radius: 6px;
						}
						.node-selected {
							outline: none;
							border: 2px solid #dadada;
							border-radius: 7px;
							border-color: #9ecaed;
							box-shadow: 0 0 10px #9ecaed;
						}
						.node-current {
							background-color: #c9def1;
							border: 1px solid lightgrey;
							padding: 5px;
							border-radius: 5px 5px 5px 5px
						}
						.cluster-summary {
							margin: 12px 16px 0 16px;
							padding: 12px 14px;
							border: 1px solid #cfd8dc;
							border-radius: 6px;
							background: linear-gradient(180deg, #f7fafc 0%, #eef3f7 100%);
							color: #37474f;
							display: flex;
							flex-wrap: wrap;
							gap: 10px 18px;
							align-items: center;
							font-size: 14px;
						}
						.cluster-summary .summary-item {
							display: inline-flex;
							align-items: center;
							gap: 6px;
						}
						.cluster-summary .summary-label {
							font-weight: 600;
							color: #455a64;
						}
						.cluster-summary .summary-warning {
							color: #c47d00;
							width: 18px;
							height: 18px;
						}
					`
				}
			]
		});
		
		
		
		
		nodeOverview.append(mainContent);

		var updateClusterSummary = function(nodes) {
			var summary = computeClusterSummary(nodes);
			var unhealthy = summary.healthy < summary.total;
			var minUpTimeDisplay = summary.minUpTime != null ? TimeUtil.milliToDisplay(summary.minUpTime) : '—';
			var maxUpTimeDisplay = summary.maxUpTime != null ? TimeUtil.milliToDisplay(summary.maxUpTime) : '—';
			var restartDisplay = summary.earliestStart
				? TimeUtil.formatDateStandard(summary.earliestStart)
				: '—';
			var warningHtml = unhealthy
				? ' <vaadin-icon icon="vaadin:warning" class="summary-warning" title="Unhealthy nodes present"></vaadin-icon>'
				: '';

			summaryStrip.innerHTML =
				'<span class="summary-item"><span class="summary-label">Live:</span> '
				+ summary.live + '/' + summary.total + '</span>'
				+ '<span class="summary-item"><span class="summary-label">Healthy:</span> '
				+ summary.healthy + '/' + summary.total + warningHtml + '</span>'
				+ '<span class="summary-item"><span class="summary-label">Uptime:</span> min '
				+ minUpTimeDisplay + ' · max ' + maxUpTimeDisplay + '</span>'
				+ '<span class="summary-item"><span class="summary-label">Last cluster restart:</span> '
				+ restartDisplay + '</span>';
		};

		var renderNodeCards = function(nodes) {
			ElementUtil.removeAllChildren(nodeCardsContainer);

			(nodes || []).forEach(function(node) {
			
				var statusBackgroundColor = 'background: linear-gradient(209deg, rgb(197 227 205) 0%, rgb(156 225 175) 42%, rgb(103 159 117) 100%);'; //'#cef7d9';
				//check if offline
				if (!node.online) {
					node.status = "OFFLINE / Failed Check In";
					statusBackgroundColor = 'background: linear-gradient(209deg, rgb(247 57 57) 0%, rgb(243 173 173) 42%, rgb(255 122 122) 100%);';
					node.offline = true;
				} else {
					if (node.userDisabled || node.systemDisabled) {
						statusBackgroundColor = 'background-color: lightgrey';
						node.status = node.status + ' (DISABLED: ' + (node.userDisabled ? 'User Disabled ' : '') +  (node.systemDisabled ? 'System Disabled ' : '') + ' )';
					} else {
						if (node.status === 'ONLINE') {
							statusBackgroundColor = 'background: linear-gradient(209deg, rgb(197 227 205) 0%, rgb(156 225 175) 42%, rgb(103 159 117) 100%);'; //#cef7d9';
						} else if (node.status === 'BUSY') {
							statusBackgroundColor = 'background: linear-gradient(209deg, rgba(255,247,208,1) 0%, rgba(255,228,67,1) 42%, rgba(247,246,206,1) 100%);';
						} 
					}
				}

				var currentNode = '';
				if (node.currentNode) {
					//currentNode = '<span class="node-current">Current</span>';
					currentNode = '-Current-';
				}

				var acceptJobs = 'All';
				var skipJobs = 'None';
				
				if (node.acceptJobs) {
					acceptJobs = node.acceptJobs;
				}
				if (node.skipJobs) {
					skipJobs = node.skipJobs;
				}
				
				var systemHealth = 'Healthy';
				if (node.systemDisableReason) {
					systemHealth = node.systemDisableReason;
				}

				var primaryDetails = `
					<div class="node-detail"><b>Start Date: </b>${TimeUtil.formatDateStandard(TimeUtil.parseDate(node.startTime))}</div>
					<div class="node-detail"><b>Uptime: </b>${TimeUtil.milliToDisplay(node.upTime)}</div>
					<div class="node-detail"><b>Last Check Date: </b>${TimeUtil.formatDateStandard(node.lastCheckInDts)}</div>
					<div class="node-detail"><b>App Version: </b>${node.appVersion}</div>
					<div class="node-detail"><b>Accept Jobs: </b>${acceptJobs}</div>	
					<div class="node-detail"><b>Skip Jobs: </b>${skipJobs}</div>
					<div class="node-detail"><b>System Health: </b>${systemHealth}</div>					
					<div class="node-status">Status: ${node.status}</div>
				`;
				
				var systemStatus = `
					<div class="node-id"></div>
					<div class="node-detail">CPU Load: ${node.cpuLoad} / ${node.cpuCount} | (${node.cpuPercentage})% <vaadin-progress-bar value="${node.cpuPercentage}" min="0" max="100"></vaadin-progress-bar> </div>
					<div class="node-detail">Memory: ${Math.floor(node.useMemory / 1024)}kb / ${Math.floor(node.maxMemory / 1024)}kb <vaadin-progress-bar value="${node.useMemory / node.maxMemory}"></vaadin-progress-bar></div>
					<div class="node-detail">Live Thread Count: ${node.liveThreadCount}</div>						
				`;
				node.panelId = ElementUtil.nextId();

				var nodeView = ElementUtil.create({
					tag: 'div',
					theme: 'padding spacing',
					style: 'margin: 20px; color: black; border: 1px solid lightgrey; border-radius: 5px; box-shadow: 6px 7px 6px -3px black; ' + statusBackgroundColor,
					children: [
						{
							tag: 'div',								
							style: 'padding: 10px;',								
							children: [
								{
									tag: 'dl-section-panel',
									itemId: node.panelId + '-panel',
									title: node.nodeId + " " + currentNode,
									closable: true,
									collapsible: true,
									collapeAtFirst: true,
									noRemoveOnClose: true,
									children: [	
										{
											tag: 'dl-flex-layout',
											slot: 'content',
											children: [
												{
													tag: 'div',
													slot: 'primary',
													trustedInnerHTML: true,
													innerHTML: primaryDetails
												},
												{
													tag: 'dl-toolbar',
													theme: 'spacing',
													slot: 'footer',
													'class': node.offline ? 'hide' : '',
													listeners: {
														'action-menu-open': function(event){												
															event.target.parentNode.classList.add('node-selected');
														},
														'action-menu-close': function(event){
															event.target.parentNode.classList.remove('node-selected');
														}
													},
													children: [
														{
															tag: 'vaadin-button',												
															'class': 'spacer-toolbar-item',
															theme: 'contrast primary',
															children:[
																ElementUtil.makeIcon('vaadin:cogs', ' Details')
															], 
															listeners: {
																click: function() {
																	actionNodeDetails(node);
																}
															}
														},
														{
															tag: 'vaadin-button',												
															'class': 'spacer-toolbar-item',
															theme: 'contrast',
															children:[
																ElementUtil.makeIcon('vaadin:check', ' Disable/Enable')
															], 
															listeners: {
																click: function() {
																	var loadMask = nodeOverview.showLoading();
																	if (node.userDisabled) {
																		Service.getNodeService().enableNode(node.nodeId).then(function(view){
																			loadMask.remove();
																			actionRefresh();
																		});
																	} else {
																		Service.getNodeService().disableNode(node.nodeId).then(function(view){
																			loadMask.remove();
																			actionRefresh();
																		});
																	}														
																}
															}
														},
														{
															tag: 'vaadin-button',												
															'class': 'spacer-toolbar-item',
															theme: 'primary',
															children:[
																ElementUtil.makeIcon('vaadin:check', ' Update Job Control')
															], 
															listeners: {
																click: function() {
																	actionUpdateJobs(node);														
																}
															}
														}
													]
												}		
											]	
										}										
									],
									listeners: {
										close: function() {
											actionDeleteNode(node);
										}
									}
								},
								{
									tag: 'div',
									style: 'padding: 10px;',
									trustedInnerHTML: true,
									innerHTML: systemStatus
								}
							]
						}							
					]
				});

				nodeCardsContainer.append(nodeView);
			});
		};
		
		
		var actionRefresh = function(options) {
			var silent = options && options.silent;
			var loadMask = silent ? null : nodeOverview.showLoading();
			Service.getNodeService().getNodes().then(function(nodes){
				if (loadMask) {
					loadMask.remove();
				}
				latestNodes = nodes || [];
				updateClusterSummary(latestNodes);
				renderNodeCards(latestNodes);
			});
			
		};
		actionRefresh();

		var buildScopeItems = function(nodes) {
			var items = [
				{ code: SCOPE_CLUSTER, description: SCOPE_CLUSTER }
			];
			(nodes || []).forEach(function(node) {
				items.push({
					code: node.nodeId,
					description: node.nodeId + (node.online ? '' : ' (offline)')
				});
			});
			return items;
		};

		var buildTrendChartConfig = function(trendBuffer, chartType, scopeLabel, selectedMetrics) {
			var selectedChartType = chartType || CHART_TYPE_LINE;
			var isFill = selectedChartType === CHART_TYPE_FILL_LINE;
			var metrics = selectedMetrics || { cpu: true, memory: true, threads: false };
			var labels = trendBuffer.map(function(point) {
				return TimeUtil.formatTimeOnly(point.timestamp);
			});
			var makeDataset = function(label, data, colorIndex) {
				var color = DATASET_COLORS[colorIndex % DATASET_COLORS.length];
				return {
					label: label,
					data: data,
					borderColor: color,
					backgroundColor: color,
					borderWidth: 2,
					tension: 0.2,
					fill: isFill
				};
			};

			var datasets = [];
			if (metrics.cpu) {
				datasets.push(makeDataset('CPU % (' + scopeLabel + ')', trendBuffer.map(function(point) {
					return Number(point.cpuPct.toFixed(2));
				}), 0));
			}
			if (metrics.memory) {
				datasets.push(makeDataset('Memory % (' + scopeLabel + ')', trendBuffer.map(function(point) {
					return Number(point.memoryPct.toFixed(2));
				}), 1));
			}
			if (metrics.threads) {
				datasets.push(makeDataset('Threads (' + scopeLabel + ')', trendBuffer.map(function(point) {
					return Number(point.threads.toFixed(1));
				}), 2));
			}

			var yAxisTitle = 'Value';
			if (metrics.threads && !metrics.cpu && !metrics.memory) {
				yAxisTitle = 'Threads';
			} else if ((metrics.cpu || metrics.memory) && !metrics.threads) {
				yAxisTitle = 'Percent';
			}

			return {
				type: 'line',
				data: {
					labels: labels,
					datasets: datasets
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: {
							display: true,
							position: 'bottom'
						}
					},
					scales: {
						y: {
							beginAtZero: true,
							title: {
								display: true,
								text: yAxisTitle
							}
						},
						x: {
							title: {
								display: true,
								text: 'Time'
							}
						}
					}
				}
			};
		};

		var actionShowVisualization = function() {
			var visualizeAutoRefreshIntervalId = null;

			var stopVisualizeAutoRefresh = function() {
				if (visualizeAutoRefreshIntervalId) {
					clearInterval(visualizeAutoRefreshIntervalId);
					visualizeAutoRefreshIntervalId = null;
				}
			};

			var chartWin = ElementUtil.create({
				tag: 'dl-dialog',
				title: 'Cluster Health',
				icon: 'vaadin:chart',
				scope: 'content',
				width: .75,
				height: .75,
				fillContent: true,
				listeners: {
					beforeclose: function() {
						stopVisualizeAutoRefresh();
					},
					ready: function() {
						chartWin.show();

						var selectedScope = SCOPE_CLUSTER;
						var selectedChartType = CHART_TYPE_LINE;
						var selectedMetrics = { cpu: true, memory: true, threads: false };
						var trendBuffer = [];
						var chartPanel = null;
						var scopeCombo = null;

						var refreshChartFromBuffer = function() {
							if (!chartPanel) {
								return;
							}
							chartPanel.updateChart(buildTrendChartConfig(
								trendBuffer,
								selectedChartType,
								selectedScope,
								selectedMetrics
							));
						};

						var ensureAtLeastOneMetric = function(changedMetric, checked) {
							selectedMetrics[changedMetric] = checked;
							if (!selectedMetrics.cpu && !selectedMetrics.memory && !selectedMetrics.threads) {
								selectedMetrics[changedMetric] = true;
								return false;
							}
							return true;
						};

						var clearTrendAndRefresh = function() {
							trendBuffer = [];
							refreshChartFromBuffer();
						};

						var pollAndAppendSample = function(options) {
							var silent = !options || options.silent !== false;
							var loadMask = silent ? null : chartWin.showLoading();
							Service.getNodeService().getNodes().then(function(nodes) {
								if (loadMask) {
									loadMask.remove();
								}
								latestNodes = nodes || [];
								updateClusterSummary(latestNodes);
								if (scopeCombo) {
									var items = buildScopeItems(latestNodes);
									scopeCombo.items = items;
									var stillValid = items.some(function(item) {
										return item.code === selectedScope;
									});
									if (!stillValid) {
										selectedScope = SCOPE_CLUSTER;
										scopeCombo.value = SCOPE_CLUSTER;
										clearTrendAndRefresh();
									}
								}
								var sample = computeStatsSample(latestNodes, selectedScope);
								appendTrendPoint(trendBuffer, sample, MAX_TREND_POINTS);
								refreshChartFromBuffer();
							});
						};

						var startVisualizeAutoRefresh = function() {
							stopVisualizeAutoRefresh();
							visualizeAutoRefreshIntervalId = setInterval(function() {
								pollAndAppendSample({ silent: true });
							}, AUTO_REFRESH_MS);
						};

						scopeCombo = ElementUtil.create({
							tag: 'vaadin-combo-box',
							itemid: 'scopeSelection',
							'class': 'spacer-toolbar-item',
							style: 'width: 240px;',
							label: 'Scope',
							'item-value-path': 'code',
							'item-label-path': 'description',
							postinit: function(combo) {
								combo.items = buildScopeItems(latestNodes);
								combo.value = SCOPE_CLUSTER;
							},
							listeners: {
								change: function() {
									var nextScope = scopeCombo.value || SCOPE_CLUSTER;
									if (nextScope === selectedScope) {
										return;
									}
									selectedScope = nextScope;
									clearTrendAndRefresh();
									pollAndAppendSample({ silent: true });
								}
							}
						});

						var chartTypeCombo = ElementUtil.create({
							tag: 'vaadin-combo-box',
							itemid: 'chartType',
							'class': 'spacer-toolbar-item',
							style: 'width: 160px;',
							label: 'Chart Type',
							'item-value-path': 'code',
							'item-label-path': 'description',
							postinit: function(combo) {
								combo.items = [
									{ code: CHART_TYPE_LINE, description: 'Line' },
									{ code: CHART_TYPE_FILL_LINE, description: 'Fill Line' }
								];
								combo.value = CHART_TYPE_LINE;
							},
							listeners: {
								change: function() {
									selectedChartType = chartTypeCombo.value || CHART_TYPE_LINE;
									refreshChartFromBuffer();
								}
							}
						});

						var autoRefreshCheckbox = ElementUtil.create({
							tag: 'vaadin-checkbox',
							itemid: 'visualizeAutoRefresh',
							'class': 'spacer-toolbar-item',
							checked: true,
							label: 'Auto refresh (10s)',
							listeners: {
								change: function(event) {
									if (event.target.checked) {
										startVisualizeAutoRefresh();
									} else {
										stopVisualizeAutoRefresh();
									}
								}
							}
						});

						var createMetricCheckbox = function(itemid, label, metricKey, checked) {
							return ElementUtil.create({
								tag: 'vaadin-checkbox',
								itemid: itemid,
								'class': 'spacer-toolbar-item',
								checked: checked,
								label: label,
								listeners: {
									change: function(event) {
										var allowed = ensureAtLeastOneMetric(metricKey, event.target.checked);
										if (!allowed) {
											event.target.checked = true;
											window.DL.MessageUtil.showToast('Chart Metrics', 'Select at least one metric', {
												theme: 'contrast',
												timeout: 3000
											});
											return;
										}
										refreshChartFromBuffer();
									}
								}
							});
						};

						var cpuMetricCheckbox = createMetricCheckbox('metricCpu', 'CPU', 'cpu', true);
						var memoryMetricCheckbox = createMetricCheckbox('metricMemory', 'Memory', 'memory', true);
						var threadsMetricCheckbox = createMetricCheckbox('metricThreads', 'Threads', 'threads', false);

						var dialogToolbar = ElementUtil.create({
							tag: 'dl-toolbar',
							theme: 'spacing',
							children: [
								scopeCombo,
								chartTypeCombo,
								cpuMetricCheckbox,
								memoryMetricCheckbox,
								threadsMetricCheckbox,
								autoRefreshCheckbox,
								{
									tag: 'vaadin-button',
									itemid: 'sampleNow',
									'class': 'spacer-toolbar-item',
									children: [
										ElementUtil.makeIcon('vaadin:refresh', ' Sample Now')
									],
									listeners: {
										click: function() {
											pollAndAppendSample({ silent: false });
										}
									}
								}
							]
						});

						chartPanel = ElementUtil.create({
							tag: 'dl-chart-panel',
							style: 'flex: 1 1 auto; min-height: 320px; width: 100%;',
							listeners: {
								ready: function() {
									pollAndAppendSample({ silent: true });
									startVisualizeAutoRefresh();
								}
							}
						});

						var contentLayout = ElementUtil.create({
							tag: 'div',
							style: 'display: flex; flex-direction: column; height: 100%; width: 100%; gap: 8px; box-sizing: border-box; padding: 8px;',
							children: [
								dialogToolbar,
								chartPanel
							]
						});
						chartWin.addToContentElement(contentLayout);
					}
				}
			});
		};

		var actionDownloadLogs = function() {
			var today = TimeUtil.formatDateForTimeField(new Date());
			var dialog = ElementUtil.create({
				tag: 'dl-dialog',
				title: 'Download Node Logs',
				icon: 'vaadin:download',
				width: 420,
				height: 320,
				listeners: {
					ready: function() {
						dialog.show();

						var dialogContent = ElementUtil.create({
							tag: 'div',
							style: 'padding: 12px; display: flex; flex-direction: column; gap: 10px;',
							children: [
								{
									tag: 'vaadin-checkbox',
									itemid: 'currentOnly',
									checked: true,
									label: 'Current log only (spring.log)',
									listeners: {
										change: function(event) {
											var checked = event.target.checked;
											ElementUtil.query(dialogContent, 'startDate').disabled = checked;
											ElementUtil.query(dialogContent, 'endDate').disabled = checked;
										}
									}
								},
								{
									tag: 'vaadin-date-picker',
									itemid: 'startDate',
									label: 'Start Date',
									value: today,
									disabled: true
								},
								{
									tag: 'vaadin-date-picker',
									itemid: 'endDate',
									label: 'End Date',
									value: today,
									disabled: true
								},
								{
									tag: 'div',
									style: 'text-align: center; margin-top: 8px;',
									defaults: {
										tag: 'vaadin-button'
									},
									children: [
										{
											theme: 'primary',
											children: [ElementUtil.makeIcon('vaadin:download', ' Start')],
											listeners: {
												click: function() {
													var currentOnly = ElementUtil.query(dialogContent, 'currentOnly').checked;
													var payload = {
														currentOnly: currentOnly,
														startDate: ElementUtil.query(dialogContent, 'startDate').value,
														endDate: ElementUtil.query(dialogContent, 'endDate').value
													};
													dialog.close();
													Service.getNodeService().submitNodeLogPackage(payload).then(function(result) {
														var message = (result && result.data)
															? result.data
															: 'Node logs package is building in the background.';
														window.DL.MessageUtil.showToast('Download', message, {
															timeout: 8000,
															theme: 'primary'
														});
													});
												}
											}
										},
										{
											theme: 'tertiary',
											style: 'margin-left: 10px;',
											children: [ElementUtil.makeIcon('vaadin:close', ' Cancel')],
											listeners: {
												click: function() {
													dialog.close();
												}
											}
										}
									]
								}
							]
						});
						dialog.addToContentElement(dialogContent);
					}
				}
			});
		};
		
		
		var actionNodeDetails = function(node) {
			
			var detailsWin = ElementUtil.create({
				tag: 'dl-dialog',
				title: 'Details for ' + node.nodeId,
				icon: 'vaadin:cogs',
				width: .9,
				height: .9,
				listeners: {
					ready: function() {
						detailsWin.show();
						
						var tabPanel = ElementUtil.create({
							tag: 'dl-tabpanel',
							listeners: {
								ready: function(){
									tabPanel.addTabs([
										{
											'tab-title': 'Details',
											tag: 'div',
											style: 'height: 100%;',											
											children: [
												{
													tag: 'dl-node-details',
													nodeId: node.nodeId,
													listeners: {
														'deleteNode': function() {
															detailsWin.close();
															actionRefresh();
														}
													}
												}
											]
										},
										{
											'tab-title': 'Threads',
											tag: 'div',
											style: 'height: 100%;',											
											children: [
												{
													tag: 'dl-thread-grid',
													nodeId: node.nodeId
												}
											]
										},
										{
											'tab-title': 'Instance Properties',
											tag: 'div',
											style: 'height: 100%;',											
											children: [
												{
													tag: 'dl-properties',
													local: true,
													nodeId: node.nodeId
												}
											]
										},
										{
											'tab-title': 'Managers',
											tag: 'div',
											style: 'height: 100%;',											
											children: [
												{
													tag: 'dl-manager-grid',
													nodeId: node.nodeId
												}
											]											
										},
										{
											'tab-title': 'Caches',
											tag: 'div',											
											style: 'height: 100%;',											
											children: [
												{
													tag: 'dl-cachingcontrol-grid',
													nodeId: node.nodeId
												}
											]											
										},
										{
											'tab-title': 'Logs',
											tag: 'div',
											style: 'height: 100%;',											
											children: [
												{
													tag: 'dl-logs-grid',
													nodeId: node.nodeId
												}
											]
										},
										{
											'tab-title': 'Background Tasks',
											tag: 'div',
											style: 'height: 100%;',											
											children: [
												{
													tag: 'dl-backgroundtasks-grid',
													nodeId: node.nodeId
												}
											]
										},
										{
											'tab-title': 'Instance Jobs',
											tag: 'div',											
											style: 'height: 100%;',											
											children: [
												{
													tag: 'dl-job-grid',
													local: true,
													nodeId: node.nodeId
												}
											]											
										},
										{
											'tab-title': 'File Stats',
											tag: 'div',											
											style: 'height: 100%;',											
											children: [
												{
													tag: 'dl-file-stats',
													local: true,
													nodeId: node.nodeId
												}
											]
										}
										
									]);
								}
							}
						});
						
						detailsWin.addToContentElement(tabPanel);	
						
					}
				}
			});
						
		};
		
		var actionUpdateJobs = function(node) {
			
			var editWin = ElementUtil.create({
				tag: 'dl-dialog',
				title: 'Update Node Jobs',
				icon:  'vaadin:edit',
				width: 500,
				height: 275,
				listeners: {
					ready: function() {						
						editWin.show();
						
						var formPanel = ElementUtil.create({
							tag: 'dl-form-panel',
							itemid: 'form',
							children: [
								{
									tag: 'vaadin-form-layout',										
									children: [
										{
											tag: 'vaadin-text-field',										
											name: 'acceptJobs',
											label: 'Accept Jobs',
											'helper-text': 'Job Name#Group contains value; Comma-separated',
											colspan: 2,
											maxLength: 1024											
										},
										{
											tag: 'vaadin-text-field',										
											name: 'skipJobs',
											label: 'Skip Jobs',											
											'helper-text': 'Job Name#Group contains value; Comma-separated',
											colspan: 2,
											maxLength: 1024											
										},							
										{
											tag: 'hr',
											colspan: 2
										},
										{
											tag: 'div',
											style: 'text-align: center;',
											colspan: 2,
											defaults: {
												tag: 'vaadin-button'
											},
											children: [
												{
													theme: 'primary',
													children: [
														ElementUtil.makeIcon('vaadin:database', ' Save')
													],
													listeners: {
														click: function() {															
																												
															formPanel.submitForm({
																preSubmit: function(data) {																	
																	return data;
																},
																actualCall: function(data) {			
																	return Service.getNodeService().updateNodeJobs(node.nodeId, data);
																},
																success: function(data, responseData) {
																	window.DL.MessageUtil.showToast('Success', 'Updated Node', {
																		theme: 'success',
																		position: 'top-stretch'
																	});
																	actionRefresh();
																	editWin.close();
																}
															});															
															
														}
													}
												},
												{
													theme: 'tertiary',
													innerHTML: 'Cancel',
													style: 'margin-left: 10px;',
													listeners: {
														click: function() {
															//cancel
															editWin.close();
														}
													}
												}
											]
										}
									]
								}
							]
						});			
												
						editWin.addToContentElement(formPanel);
						
						var loadModel = {
							acceptJobs: node.acceptJobs,
							skipJobs: node.skipJobs
						};
						formPanel.load(loadModel);						
					}
				}
			});	
			
		};
		
		var actionDeleteNode = function(node) {
			
			window.DL.MessageUtil.showConfirm({
				title: 'Confirm Delete Node',
				message: 'Are you sure you want to delete node record? <br> <br> (This will only delete the current record. Live nodes will recreate the record.)',				
				buttons: [
					{
						text: 'Yes',
						handler: function() {
							var nodePanel = ElementUtil.query(nodeCardsContainer, node.panelId + '-panel');
							
							var loadMask = nodePanel.showLoading('Deleting...');
							Service.getNodeService().deleteNode(node.nodeId).then(function(detailInfo){
								loadMask.remove();
								window.DL.MessageUtil.showToast('Success', 'Deleted Node', {									
									theme: 'success'
								});
								
								actionRefresh();
							});							
						}
					},
					{
						text: 'No'
					}
				]
			});
			
		};		
		
		
	}	
	
	
};
customElements.define('dl-node-overview', NodeOverview);
